import React from 'react';
import { fmtDate, parseDate, timelinePosition } from './dates';

// PRD-030 WU-10 — the shared agreement facts strip + timeline (F7a).
// Built to mockup-verification-harness-prd030.html (rev 2): the facts row
// (Agreement, Signed, Term, Value, Today) over a rail running signed →
// today → each Outcome's due date. Identical for Agency and Client.
// Rendered in the app's light design system (CLAUDE.md design north star);
// structure, content, and copy match the ratified mockup.

function centsToMoney(cents, currency = 'USD') {
  if (cents == null) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function Fact({ k, v }) {
  if (!v) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide font-bold text-gray-600">{k}</div>
      <div className="text-sm font-semibold text-gray-900 tabular-nums mt-0.5">{v}</div>
    </div>
  );
}

export default function AgreementFactsTimeline({ pnl }) {
  const t = pnl.timeline || {};
  const signedAt = pnl.signed_at || t.buyer_signed_at || t.seller_signed_at || null;
  const termStart = t.term_starts_at || signedAt;
  const termEnd = t.term_ends_at || null;
  const today = new Date();

  // Timeline points: signed → today → each Outcome due date (active_to),
  // deduplicated and positioned along the term.
  const points = [];
  if (signedAt) points.push({ key: 'signed', label: `Signed ${fmtDate(signedAt, { year: false })}`, at: signedAt, kind: 'done' });
  points.push({ key: 'today', label: `Today ${fmtDate(today, { year: false })}`, at: today.toISOString(), kind: 'now' });
  for (const o of pnl.outcomes || []) {
    if (!o.active_to) continue;
    const due = parseDate(o.active_to);
    points.push({
      key: `due-${o.outcome_id}`,
      label: `${shortName(o.name)} due ${fmtDate(o.active_to, { year: false })}`,
      at: o.active_to,
      dateLabel: fmtDate(o.active_to, { year: false }),
      // A past due date is NOT delivery — the dot stays neutral; delivery
      // status lives on the status cards, never implied by dot color.
      kind: due < today ? 'past' : 'future',
      mergeable: true,
    });
  }
  points.sort((a, b) => new Date(a.at) - new Date(b.at));

  const canRail = termStart && termEnd;
  const pos = (at) => 2 + timelinePosition(at, termStart, termEnd) * 96; // 2%..98%
  const todayPct = canRail ? timelinePosition(today, termStart, termEnd) * 100 : 0;

  // Caption collisions: Outcome due points sharing a CALENDAR DATE merge
  // into one dot ("2 outcomes due Sep 16" — always that date's own label,
  // never a neighbor's). Signed/Today never merge. Remaining near-misses
  // stagger their captions onto a second row so no label ever overlaps.
  const merged = [];
  for (const p of points) {
    const prev = merged[merged.length - 1];
    if (prev && p.mergeable && prev.mergeable && p.dateLabel === prev.dateLabel) {
      prev.count = (prev.count || 1) + 1;
      prev.label = `${prev.count} outcomes due ${prev.dateLabel}`;
      if (p.kind === 'future') prev.kind = 'future';
    } else {
      merged.push({ ...p });
    }
  }
  for (let i = 1; i < merged.length; i += 1) {
    if (pos(merged[i].at) - pos(merged[i - 1].at) < 9 && !merged[i - 1].staggered) {
      merged[i].staggered = true; // caption drops to the second row
    }
  }

  return (
    <div className="card p-4">
      <div className="flex flex-wrap gap-x-6 gap-y-3">
        <Fact k="Agreement" v={pnl.name} />
        <Fact k="Signed" v={fmtDate(signedAt)} />
        <Fact k="Term" v={termStart && termEnd ? `${fmtDate(termStart, { year: false })} – ${fmtDate(termEnd)}` : null} />
        <Fact k="Value" v={centsToMoney(pnl.totals?.revenue_cents, pnl.currency)} />
        <Fact k="Today" v={fmtDate(today)} />
      </div>
      {canRail && (
        <div className="relative h-16 mt-4 mx-1" data-testid="agreement-timeline">
          <div className="absolute top-4 left-0 right-0 h-[3px] bg-gray-200 rounded" />
          <div className="absolute top-4 left-0 h-[3px] bg-primary-500 rounded" style={{ width: `${todayPct}%` }} />
          {merged.map((p) => {
            const pct = pos(p.at);
            // WU-B defect 4 — captions must never overrun the card. A
            // center-anchored nowrap caption under a dot near either rail
            // end extends past the edge ("2 outcomes due Oct 22" clipped
            // on staging). Near the edges the caption anchors to the dot's
            // inner side instead; the dot itself stays on the rail point.
            const anchor = pct > 86 ? 'right' : pct < 12 ? 'left' : 'center';
            const captionCls =
              anchor === 'right'
                ? 'right-0 text-right'
                : anchor === 'left'
                  ? 'left-0 text-left'
                  : 'left-1/2 -translate-x-1/2 text-center';
            return (
              <div key={p.key} className="absolute top-[9px] -translate-x-1/2" style={{ left: `${pct}%` }}>
                <div
                  className={`w-[11px] h-[11px] rounded-full border-2 border-white ${
                    p.kind === 'now'
                      ? 'bg-primary-600 ring-4 ring-primary-100'
                      : p.kind === 'done'
                        ? 'bg-emerald-500'
                        : 'bg-gray-300'
                  }`}
                />
                <div
                  className={`absolute text-[10px] whitespace-nowrap ${captionCls} ${p.staggered ? 'top-[33px]' : 'top-[17px]'} ${
                    p.kind === 'now' ? 'text-primary-600 font-bold' : 'text-gray-600'
                  }`}
                >
                  {p.label}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// "10,000 verified app installs" → "Installs"-style short label for the
// rail caption; falls back to the first two words.
function shortName(name) {
  if (!name) return 'Outcome';
  const words = String(name).replace(/^[\d,.]+\s*/, '').split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'Outcome';
  const first = words[0];
  return first.charAt(0).toUpperCase() + first.slice(1, 11);
}
